import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../../api/client';
import type { RootState } from '../index';

export const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'SGD', 'KRW', 'INR', 'BRL'] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export const CURRENCY_META: Record<
  SupportedCurrency,
  { symbol: string; name: string; locale: string }
> = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB' },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
  CAD: { symbol: 'CA$', name: 'Canadian Dollar', locale: 'en-CA' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  KRW: { symbol: '₩', name: 'South Korean Won', locale: 'ko-KR' },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
};

const LS_KEY = 'preferredCurrency';

function loadFromStorage(): SupportedCurrency {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && SUPPORTED_CURRENCIES.includes(stored as SupportedCurrency)) {
      return stored as SupportedCurrency;
    }
  } catch {
    // ignore
  }
  return 'USD';
}

export interface CurrencyState {
  preferredCurrency: SupportedCurrency;
  fxRates: Record<string, number>;
  fxRatesLoading: boolean;
  fxRatesError: string | null;
  lastFetched: number | null;
  cachedAt: string | null;
  ttlMs: number;
  source: 'redis' | 'api' | 'fallback' | null;
}

const initialState: CurrencyState = {
  preferredCurrency: loadFromStorage(),
  fxRates: { USD: 1 },
  fxRatesLoading: false,
  fxRatesError: null,
  lastFetched: null,
  cachedAt: null,
  ttlMs: 60 * 60 * 1000,
  source: null,
};

export interface FxRatesResponse {
  base: string;
  rates: Record<string, number>;
  supportedCurrencies: string[];
  cachedAt: string | null;
  ttlMs: number;
  source: 'redis' | 'api' | 'fallback';
}

export const fetchFxRates = createAsyncThunk<
  FxRatesResponse,
  { force?: boolean } | void,
  { state: RootState }
>(
  'currency/fetchFxRates',
  async (args, { getState }) => {
    const { lastFetched, ttlMs } = getState().currency;
    const force = args && typeof args === 'object' ? Boolean(args.force) : false;
    // Skip refetch if rates are fresh
    if (!force && lastFetched && Date.now() - lastFetched < ttlMs) {
      throw new Error('SKIP'); // signals reducer to no-op
    }
    const response = await apiClient.get<FxRatesResponse>('/prices/fx-rates');
    return response.data;
  }
);

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    setPreferredCurrency(state, action: PayloadAction<SupportedCurrency>) {
      state.preferredCurrency = action.payload;
      try {
        localStorage.setItem(LS_KEY, action.payload);
      } catch {
        // ignore
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFxRates.pending, (state) => {
        state.fxRatesLoading = true;
        state.fxRatesError = null;
      })
      .addCase(fetchFxRates.fulfilled, (state, action) => {
        state.fxRatesLoading = false;
        if (action.payload.rates && typeof action.payload.rates === 'object') {
          state.fxRates = action.payload.rates;
        }
        state.lastFetched = Date.now();
        state.cachedAt = action.payload.cachedAt;
        state.ttlMs = action.payload.ttlMs ?? 60 * 60 * 1000;
        state.source = action.payload.source;
      })
      .addCase(fetchFxRates.rejected, (state, action) => {
        state.fxRatesLoading = false;
        // SKIP is a deliberate no-op, not an error
        if (action.error.message !== 'SKIP') {
          state.fxRatesError = action.error.message ?? 'Failed to fetch FX rates';
        }
      });
  },
});

export const { setPreferredCurrency } = currencySlice.actions;
export default currencySlice.reducer;

// Selectors
export const selectPreferredCurrency = (state: RootState) => state.currency.preferredCurrency;
export const selectFxRates = (state: RootState) => state.currency.fxRates;
export const selectFxRatesLoading = (state: RootState) => state.currency.fxRatesLoading;
export const selectFxRatesError = (state: RootState) => state.currency.fxRatesError;
export const selectFxCachedAt = (state: RootState) => state.currency.cachedAt;
export const selectFxTtlMs = (state: RootState) => state.currency.ttlMs;
export const selectFxSource = (state: RootState) => state.currency.source;
